import { useCallback } from "react";
import { notification } from "antd";
import { SchemaFormAction } from "../../../../components/forms/FormActionNew/FormAction";
import { Action, FormActionPostValues } from "../../../../types/actions";
import { formatDayJSToDateString } from "../../../../utils/dates";
import { getErrorMessage } from "../../../../utils/errors/errors";
import { useNavigate } from "react-router-dom";
import {
  useActionCreateMutation,
  useActionDeleteMutation,
  useActionUpdateMutation,
  useActionUploadAttachmentsMutation,
  useFetchActionCommentsQuery,
  useCreateActionCommentMutation,
} from "../../apiSlice";
import { FetchActionsQuery } from "../../../../types/reactQueries";
import { CommentPostValues } from "../../../../types/comments";

// Accepts the refetch fn from any parent entity query (Event, Constat, Project...);
// only `.unwrap()` is used, so the resolved data's shape doesn't matter here.
type RefetchParentQuery = () => { unwrap: () => Promise<unknown> };

export const useCrudAction = (
  fetchPaginatedActions?: FetchActionsQuery,
  refetchEvent?: RefetchParentQuery,
  eventId?: number,
  isEditMode?: boolean,
  action?: Action,
  parentType: "event" | "constat" | "project" = "event",
) => {
  const navigate = useNavigate();
  const parentBasePath =
    parentType === "constat"
      ? "/constats"
      : parentType === "project"
        ? "/projects"
        : "/events";

  const [lazyDeleteAction, { isLoading: isDeleting }] =
    useActionDeleteMutation();

  const [actionCreate, { isLoading: isCreating }] = useActionCreateMutation();
  const [updateAction, { isLoading: isUpdating }] = useActionUpdateMutation();
  const [postAttachments, { isLoading: isPostingAttachments }] =
    useActionUploadAttachmentsMutation();

  const _createAction = useCallback(
    async (values: SchemaFormAction, stayToCreateAfterSuccess: boolean) => {
      try {
        console.log("Submit", values);

        // 1) Format values to match the server's expectations
        const formattedValues: FormActionPostValues = {
          ...values,
          date: formatDayJSToDateString(values.date),
          dateAsked: formatDayJSToDateString(values.dateAsked),
          performer: Number(values.performer),
          event: Number(values.event),
        };
        console.log("formattedValues", formattedValues);

        // 2) Create action
        const createdAction = await actionCreate(formattedValues).unwrap();
        console.log("createdAction: ", createdAction);

        // 3) Success notification for action creation
        notification.success({
          message: "Action crÃ©Ã©e",
          description: "L'action a Ã©tÃ© crÃ©Ã©e avec succÃ¨s.",
        });

        const files = values?.attachedFiles;
        if (files?.length) {
          await postAttachments({
            actionId: createdAction.id,
            files,
          });
          // 4) Success notification for files upload
          notification.success({
            message: "PiÃ¨ces jointes ajoutÃ©es",
          });
        }
        if (stayToCreateAfterSuccess) {
          window.location.reload();
        } else {
          navigate(
            `${parentBasePath}/${formattedValues.event}/actions/${createdAction.id}`,
          );
        }
      } catch (error) {
        console.error("Create action error", error);
        notification.error({
          message: "Erreur",
          description: getErrorMessage(error),
        });
      }
    },
    [actionCreate, navigate, postAttachments, parentBasePath],
  );

  const _updateAction = useCallback(
    async (values: SchemaFormAction) => {
      try {
        if (!action?.id) {
          throw new Error("Action id is missing");
        }

        // 1) Format values to match the server's expectations
        const formattedValues: FormActionPostValues = {
          ...values,
          date: formatDayJSToDateString(values.date),
          dateAsked: formatDayJSToDateString(values.dateAsked),
          performer: Number(values.performer),
          event: Number(values.event),
        };

        console.log("Submit updateAction", values);
        console.log("formattedValues", formattedValues);

        // 2) Update action
        const updatedAction = await updateAction({
          actionId: action.id,
          values: formattedValues,
        }).unwrap();

        console.log("updateAction repsonse: ", updatedAction);

        // 3) Success notification for action creation
        notification.success({
          message: "Action modifiÃ©e",
          description: "L'action a Ã©tÃ© modifiÃ©e avec succÃ¨s.",
        });

        // 4) Upload attachments (if any new files selected)
        const files = values?.attachedFiles;
        if (files?.length) {
          await postAttachments({
            actionId: action.id,
            files,
          });
          notification.success({
            message: "PiÃ¨ces jointes ajoutÃ©es",
          });
        }

        navigate(
          `${parentBasePath}/${formattedValues.event}/actions/${updatedAction.id}`,
        );
      } catch (error) {
        console.error("Update action error", error);
        notification.error({
          message: "Erreur",
          description: getErrorMessage(error),
        });
      }
    },
    [action?.id, updateAction, navigate, parentBasePath, postAttachments],
  );

  const onDelete = useCallback(
    async (id: number) => {
      try {
        console.log("deleted action id", id);
        await lazyDeleteAction(id).unwrap();
        // Success notification
        notification.success({
          message: "Action supprimÃ©e",
          description: `L'action a Ã©tÃ© supprimÃ©e avec succÃ¨s.`,
        });
        if (fetchPaginatedActions) {
          await fetchPaginatedActions("").unwrap();
        } else if (refetchEvent && eventId) {
          await refetchEvent().unwrap();
        }
      } catch (error) {
        console.error("deleteAction error", error);
        notification.error({
          message: "Erreur",
          description: getErrorMessage(error),
        });
      }
    },
    [lazyDeleteAction, fetchPaginatedActions, refetchEvent, eventId],
  );

  /**
   * Comments
   */
  const { data: comments, refetch: refetchComments } = useFetchActionCommentsQuery(
    action?.id as number,
    { skip: !action?.id },
  );
  const [createCommentRequest, { isLoading: isCreatingComment }] =
    useCreateActionCommentMutation();
  const createComment = useCallback(
    async (values: Pick<CommentPostValues, 'description'>) => {
      if (!action?.id) return;
      try {
        await createCommentRequest({
          description: values.description,
          commentType: 'ACTION',
          action: action.id,
        }).unwrap();
        notification.success({ message: "Commentaire ajoutÃ©." });
        refetchComments();
      } catch (error) {
        notification.error({
          message: "Erreur",
          description: getErrorMessage(error),
        });
      }
    },
    [createCommentRequest, action?.id, refetchComments],
  );

  return {
    onDelete,
    isDeleting: isDeleting,
    onSubmit: isEditMode ? _updateAction : _createAction,
    isLoading: isCreating || isUpdating,
    isPostingAttachments,
    // Comments
    comments,
    createComment,
    isCreatingComment,
  };
};
